import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Admin.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND}/api`;
const TABS = ['Pages', 'SEO', 'Content', 'Timers & Coupons', 'Analytics', 'Inbox'];
const PAGE_KEYS = ['home', 'performance-reset', 'performance-resetads', 'thank-you'];

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', ...opts });
  if (!r.ok) {
    let detail = `${r.status}`;
    try { detail = (await r.json()).detail || detail; } catch (e) { /* noop */ }
    throw new Error(detail);
  }
  return r.json();
}

function useAuth() {
  const location = useLocation();
  const processed = useRef(false);
  const hasSessionId = location.hash?.includes('session_id=');
  const [state, setState] = useState('checking'); // checking | authed | denied | error
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (hasSessionId) {
      if (processed.current) return;
      processed.current = true;
      const sid = location.hash.split('session_id=')[1].split('&')[0];
      api('/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid }),
      }).then((u) => {
        window.history.replaceState(null, '', '/admin');
        setUser(u); setState('authed');
      }).catch((e) => setState(e.message.includes('allowlist') ? 'denied' : 'error'));
      return;
    }
    api('/auth/me').then((u) => { setUser(u); setState('authed'); })
      .catch((e) => setState(e.message.includes('allowlist') ? 'denied' : 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { state, user };
}

function LoginBox({ error }) {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="center-box">
      <div>
        <h1>The Performance Reset · Admin</h1>
        <p className="sub-h">Sign in with the Google account on the admin allowlist.</p>
        {error && <p className="err" data-testid="admin-login-error">{error}</p>}
        <button className="btn" data-testid="admin-login-button" onClick={login}>Sign in with Google</button>
      </div>
    </div>
  );
}

function PagesTab() {
  const [pages, setPages] = useState([]);
  useEffect(() => { api('/admin/pages').then((d) => setPages(d.pages)).catch(() => {}); }, []);
  return (
    <div className="card" data-testid="tab-pages">
      <h2>Pages</h2>
      <table>
        <thead><tr><th>Key</th><th>Path</th><th>Variant</th><th>SEO title</th></tr></thead>
        <tbody>
          {pages.map((p) => (
            <tr key={p.key}><td>{p.key}</td><td>{p.path}</td><td>{p.variant}</td><td>{p.seo?.title}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeoTab() {
  const [forms, setForms] = useState({});
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api('/admin/pages').then((d) => {
      const f = {};
      d.pages.forEach((p) => { f[p.key] = { title: p.seo?.title || '', description: p.seo?.description || '', og_image: p.seo?.og_image || '' }; });
      setForms(f);
    }).catch(() => {});
  }, []);
  const save = async (key) => {
    await api(`/admin/pages/${key}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ seo: forms[key] }) });
    setMsg(`Saved SEO for ${key}`);
  };
  return (
    <div data-testid="tab-seo">
      {PAGE_KEYS.map((key) => forms[key] && (
        <div className="card" key={key}>
          <h2>{key}</h2>
          <label>Title</label>
          <input type="text" data-testid={`seo-title-${key}`} value={forms[key].title}
            onChange={(e) => setForms({ ...forms, [key]: { ...forms[key], title: e.target.value } })} />
          <label>Description</label>
          <input type="text" data-testid={`seo-description-${key}`} value={forms[key].description}
            onChange={(e) => setForms({ ...forms, [key]: { ...forms[key], description: e.target.value } })} />
          <label>OG image path</label>
          <input type="text" data-testid={`seo-og-${key}`} value={forms[key].og_image}
            onChange={(e) => setForms({ ...forms, [key]: { ...forms[key], og_image: e.target.value } })} />
          <button className="btn" data-testid={`seo-save-${key}`} onClick={() => save(key)}>Save</button>
        </div>
      ))}
      {msg && <p className="msg">{msg}</p>}
    </div>
  );
}

function ContentTab() {
  const [sections, setSections] = useState(null);
  const [heroes, setHeroes] = useState({ hero_a: '', hero_b: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      const [resolved, pages] = await Promise.all([
        api('/page/resolve?slug=performance-reset'),
        api('/admin/pages'),
      ]);
      setSections(resolved.content);
      const pr = pages.pages.find((p) => p.key === 'performance-reset') || {};
      setHeroes({ hero_a: JSON.stringify(pr.hero_a || {}, null, 2), hero_b: JSON.stringify(pr.hero_b || {}, null, 2) });
    })().catch((e) => setErr(String(e.message || e)));
  }, []);

  const saveSection = async (sectionKey, value) => {
    setMsg(''); setErr('');
    try {
      const parsed = JSON.parse(value);
      await api('/admin/pages/performance-reset', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ overrides: { [sectionKey]: parsed } }),
      });
      setMsg(`Saved section "${sectionKey}" — refresh the sales page to see it.`);
    } catch (e) { setErr(`Invalid JSON or save failed: ${e.message}`); }
  };

  const saveHero = async (which) => {
    setMsg(''); setErr('');
    try {
      const parsed = JSON.parse(heroes[which]);
      await api('/admin/pages/performance-reset', {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [which]: parsed }),
      });
      setMsg(`Saved ${which === 'hero_a' ? 'Hero A (control)' : 'Hero B (test variant)'}.`);
    } catch (e) { setErr(`Invalid JSON or save failed: ${e.message}`); }
  };

  if (err && !sections) return <p className="err">{err}</p>;
  if (!sections) return <p className="sub-h">Loading content…</p>;

  return (
    <div data-testid="tab-content">
      {Object.entries(sections).map(([key, val]) => (
        <SectionEditor key={key} sectionKey={key} value={val} onSave={saveSection} />
      ))}
      <div className="card">
        <h2>Hero A — control (A/B test)</h2>
        <textarea data-testid="hero-a-editor" value={heroes.hero_a} onChange={(e) => setHeroes({ ...heroes, hero_a: e.target.value })} />
        <button className="btn" data-testid="hero-a-save" onClick={() => saveHero('hero_a')}>Save Hero A</button>
      </div>
      <div className="card">
        <h2>Hero B — test variant (A/B test)</h2>
        <textarea data-testid="hero-b-editor" value={heroes.hero_b} onChange={(e) => setHeroes({ ...heroes, hero_b: e.target.value })} />
        <button className="btn" data-testid="hero-b-save" onClick={() => saveHero('hero_b')}>Save Hero B</button>
      </div>
      {msg && <p className="msg" data-testid="content-save-msg">{msg}</p>}
      {err && <p className="err">{err}</p>}
    </div>
  );
}

function SectionEditor({ sectionKey, value, onSave }) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  return (
    <div className="card">
      <h2>{sectionKey}</h2>
      <textarea data-testid={`content-editor-${sectionKey}`} value={text} onChange={(e) => setText(e.target.value)} />
      <button className="btn" data-testid={`content-save-${sectionKey}`} onClick={() => onSave(sectionKey, text)}>Save {sectionKey}</button>
    </div>
  );
}

function TimersTab() {
  const [forms, setForms] = useState({});
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api('/admin/pages').then((d) => {
      const f = {};
      d.pages.forEach((p) => { f[p.key] = { deadline_iso: p.timer?.deadline_iso || '', coupon_code: p.timer?.coupon_code || '' }; });
      setForms(f);
    }).catch(() => {});
  }, []);
  const save = async (key) => {
    await api(`/admin/pages/${key}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ timer: forms[key] }) });
    setMsg(`Saved timer for ${key}`);
  };
  return (
    <div data-testid="tab-timers">
      {PAGE_KEYS.map((key) => forms[key] && (
        <div className="card" key={key}>
          <h2>{key}</h2>
          <label>Deadline (ISO 8601 with offset)</label>
          <input type="text" data-testid={`timer-deadline-${key}`} value={forms[key].deadline_iso} placeholder="2026-12-31T23:59:00-05:00"
            onChange={(e) => setForms({ ...forms, [key]: { ...forms[key], deadline_iso: e.target.value } })} />
          <label>Coupon code (blank for none)</label>
          <input type="text" data-testid={`timer-coupon-${key}`} value={forms[key].coupon_code}
            onChange={(e) => setForms({ ...forms, [key]: { ...forms[key], coupon_code: e.target.value } })} />
          <button className="btn" data-testid={`timer-save-${key}`} onClick={() => save(key)}>Save</button>
        </div>
      ))}
      <p className="sub-h">After the deadline passes, the page automatically shows the post-deadline price.</p>
      {msg && <p className="msg">{msg}</p>}
    </div>
  );
}

function AnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [ab, setAb] = useState(null);
  useEffect(() => {
    api('/analytics/summary').then(setSummary).catch(() => {});
    api('/abtest/stats?test_id=hero').then(setAb).catch(() => {});
  }, []);
  return (
    <div data-testid="tab-analytics">
      <div className="card">
        <h2>Last 30 days · by route</h2>
        <table>
          <thead><tr><th>Route</th><th>Pageviews</th><th>Events</th></tr></thead>
          <tbody>
            {(summary?.routes || []).map((r) => (
              <tr key={r.page}><td>{r.page}</td><td>{r.pageviews}</td><td>{r.events}</td></tr>
            ))}
            {summary && summary.routes.length === 0 && <tr><td colSpan="3">No traffic yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="card">
        <h2>A/B test · hero</h2>
        <table>
          <thead><tr><th>Arm</th><th>Views</th><th>Spot requests</th><th>Rate</th></tr></thead>
          <tbody>
            {Object.entries(ab?.arms || {}).map(([arm, s]) => (
              <tr key={arm}><td>{arm}</td><td>{s.views || 0}</td><td>{s.conversions || 0}</td><td>{((s.rate || 0) * 100).toFixed(1)}%</td></tr>
            ))}
            {ab && Object.keys(ab.arms).length === 0 && <tr><td colSpan="4">No data yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InboxTab() {
  const [rows, setRows] = useState(null);
  const [links, setLinks] = useState(null);
  useEffect(() => {
    api('/admin/requests').then((d) => setRows(d.rows)).catch(() => setRows([]));
    api('/admin/links').then(setLinks).catch(() => setLinks(null));
  }, []);
  return (
    <div data-testid="tab-inbox">
      <div className="card" data-testid="private-links">
        <h2>Private links (admin only — never shown publicly)</h2>
        <p className="sub-h">The intake form is emailed to requesters automatically. Send checkout links yourself, only after you have reviewed the intake and approved participation. Edit in Content → enrollment / single_journey / offers.</p>
        <table>
          <tbody>
            <tr><td>Jotform intake &amp; consent (both live programs)</td><td className="mono">{links?.intake_form_url || '—'}</td></tr>
            <tr><td>SamCart $399 checkout (four weeks)</td><td className="mono">{links?.checkout_url || '—'}</td></tr>
            <tr><td>SamCart $59 checkout (single journey)</td><td className="mono">{links?.single_checkout_url || '—'}</td></tr>
            <tr><td>9D app affiliate link (public button)</td><td className="mono">{links?.affiliate_url || '—'}</td></tr>
          </tbody>
        </table>
      </div>
      <div className="card">
        <h2>Spot requests</h2>
        <table>
          <thead><tr><th>Offer</th><th>Preferred date</th><th>Name</th><th>Email</th><th>Phone</th><th>Heard about it</th><th>Page</th><th>Emailed you</th><th>Confirmation sent</th><th>Created</th></tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.request_id}><td>{r.offer === 'single-journey' ? 'Single journey $59' : 'Four weeks $399'}</td><td>{r.preferred_date || '—'}</td><td>{r.full_name}</td><td>{r.email}</td><td>{r.phone}</td><td>{r.heard_from}</td><td>{r.page}</td><td>{r.notified ? 'Yes' : 'No'}</td><td>{r.confirmed ? 'Yes' : 'No'}</td><td>{String(r.created_at).slice(0, 19).replace('T', ' ')}</td></tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan="10">No requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { state, user } = useAuth();
  const [tab, setTab] = useState('Pages');

  if (state === 'checking') return <div className="admin"><div className="center-box"><p className="sub-h">Checking your session…</p></div></div>;
  if (state === 'denied') return <div className="admin"><LoginBox error="This Google account is not on the admin allowlist." /></div>;
  if (state === 'error') return <div className="admin"><LoginBox /></div>;

  const logout = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    window.location.reload();
  };

  return (
    <div className="admin" data-testid="admin-dashboard">
      <div className="shell">
        <h1>The Performance Reset · Admin</h1>
        <p className="sub-h" data-testid="admin-user-email">
          Signed in as {user?.email} · <button className="btn ghost" style={{ margin: 0, padding: '.3rem .8rem' }} data-testid="admin-logout" onClick={logout}>Log out</button>
        </p>
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} data-testid={`admin-tab-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        {tab === 'Pages' && <PagesTab />}
        {tab === 'SEO' && <SeoTab />}
        {tab === 'Content' && <ContentTab />}
        {tab === 'Timers & Coupons' && <TimersTab />}
        {tab === 'Analytics' && <AnalyticsTab />}
        {tab === 'Inbox' && <InboxTab />}
      </div>
    </div>
  );
}
